/** Must match `toko-frontend` `embedConstants.TOKO_EMBED_AUTH_MESSAGE_TYPE`. */
export const TOKO_EMBED_AUTH_MESSAGE_TYPE = "toko-embed-auth" as const;

/** Must match `toko-frontend` `embedConstants.TOKO_EMBED_CONTEXT_MESSAGE_TYPE`. */
export const TOKO_EMBED_CONTEXT_MESSAGE_TYPE = "toko-embed-context" as const;

export function isEmbeddedInTokoHost(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function graphqlHttpUrl(): string {
  const u = import.meta.env.VITE_GRAPHQL_URL?.trim();
  if (u) return u.replace(/\/$/, "");
  const b = import.meta.env.VITE_BACKEND_URL?.trim();
  if (b) return b.replace(/\/$/, "");
  return "http://localhost:8008/api";
}

export function posPublicOrigin(): string {
  const u = import.meta.env.VITE_POS_PUBLIC_ORIGIN?.trim();
  return u && u.length > 0 ? u.replace(/\/$/, "") : "http://localhost:6688";
}

/**
 * Origins of Toko shells that may embed this app via iframe postMessage.
 * Empty = accept any sender origin (dev); production should set `VITE_TRUSTED_PARENT_ORIGINS`.
 */
export function trustedParentOrigins(): string[] {
  const raw = import.meta.env.VITE_TRUSTED_PARENT_ORIGINS?.trim();
  if (!raw) return [];
  const parts = raw.split(",").map((s) => s.trim().replace(/\/$/, ""));
  return [...new Set(parts.filter(Boolean))];
}

/** Origins equivalent to `origin` for loopback hosts (parent may use localhost vs 127.0.0.1). */
function expandLoopbackOriginVariants(origin: string): string[] {
  let u: URL;
  try {
    u = new URL(origin);
  } catch {
    return [origin.replace(/\/$/, "")];
  }
  const h = u.hostname.toLowerCase();
  if (h !== "localhost" && h !== "127.0.0.1" && h !== "[::1]" && h !== "::1") {
    return [u.origin];
  }
  const protocol = u.protocol;
  const portSuffix = u.port ? `:${u.port}` : "";
  return [
    `${protocol}//localhost${portSuffix}`,
    `${protocol}//127.0.0.1${portSuffix}`,
    `${protocol}//[::1]${portSuffix}`,
  ];
}

/**
 * Whether a postMessage `event.origin` may send embed auth/context to this app.
 * When `VITE_TRUSTED_PARENT_ORIGINS` is unset (typical dev), any origin is accepted.
 * When set, loopback aliases match (e.g. parent at http://127.0.0.1:5566 vs allowlist http://localhost:5566).
 */
export function isTrustedEmbedParentOrigin(messageOrigin: string | null | undefined): boolean {
  if (messageOrigin == null || messageOrigin === "") {
    return false;
  }
  const allowed = trustedParentOrigins();
  if (allowed.length === 0) {
    return true;
  }
  const msgSet = new Set(expandLoopbackOriginVariants(messageOrigin));
  for (const entry of allowed) {
    for (const v of expandLoopbackOriginVariants(entry)) {
      if (msgSet.has(v)) {
        return true;
      }
    }
  }
  return false;
}

export function acceptLanguageFromLocale(locale: string | null | undefined): string {
  const base = locale?.trim();
  if (!base) return "ru-RU,ru;q=0.9,en;q=0.5,uz;q=0.4";
  const short = base.split("-")[0];
  if (short === base) return `${base},ru;q=0.8,en;q=0.5`;
  return `${base},${short};q=0.95,en;q=0.5`;
}
